import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  provider: string;
  createdAt: Date | null;
}

export interface UserProfileState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
}

/**
 * Subscribes to the `users/{uid}` Firestore document and returns the full
 * user profile stored there (displayName, photoURL, provider, createdAt, etc.).
 * Falls back gracefully when the document doesn't exist yet.
 */
export function useUserProfile(): UserProfileState {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const ref = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            uid: data['uid'] as string,
            email: (data['email'] as string | null) ?? null,
            displayName: (data['displayName'] as string | null) ?? null,
            photoURL: (data['photoURL'] as string | null) ?? null,
            provider: (data['provider'] as string) ?? 'unknown',
            createdAt: data['createdAt']?.toDate() ?? null,
          });
        } else {
          setProfile(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('[useUserProfile] Firestore error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [user?.uid]);

  return { profile, loading, error };
}
